import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { User } from "../../../auth/models";
import { ConfirmModalComponent } from '../../../common/components/confirm-modal/confirm-modal.component';
import { CoverLetter, Document, RequestedDocument } from '../../../common/models';
import { TenderDocumentsService } from '../../../common/services';
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import { Contract } from '../../models';
import { Tender } from '../../models/tender';
import {
  TenderBidsService,
  TenderLaunchingXlsExportService,
  TenderService,
  TendersService
} from '../../services';

@Component({
    selector: 'jft-tender-review',
    templateUrl: './create-tender-review.component.html',
    styleUrls: ['./create-tender-review.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateTenderReviewComponent implements OnInit, OnDestroy {

    public tender: Tender;
    public coverLetter: CoverLetter;
    public contracts: Contract[] = [];
    public documents: Document[] = [];
    public requestedDocuments: RequestedDocument[] = [];
    public loading: boolean = false;
    public tenderInvitedSupplierUsers: User[] = [];
    public defaultDateFormat: string = 'MMM dd, yyyy';

    // Location pagination & search
    public currentPage: number = 1;
    public pageSize: number = 30;
    public totalItems: number = 0;
    public loadingMore: boolean = false;
    public locationSearch: string = '';

    // Supplier search & load more
    public supplierSearch: string = '';
    public visibleSupplierCount: number = 30;

    private destroy$ = new Subject<void>();
    private locationSearch$ = new Subject<string>();

    constructor(
        private tenderService: TenderService,
        private tendersService: TendersService,
        private documentsService: TenderDocumentsService,
        private tenderExportXlsService: TenderLaunchingXlsExportService,
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private contractsService: TenderBidsService,
        private loadingIndicator: LoadingIndicatorService,
        private ngbModal: NgbModal,
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.snapshot.data;

        this.tender = parentData['tender'];
        this.documents = data['documents'];
        this.requestedDocuments = data['requestedDocuments'];
        this.coverLetter = data['coverLetter'];
        this.tenderInvitedSupplierUsers = data['tenderInvitedSupplierUsers'];
        this.fetchContracts(1, false);

        // Debounce location search to avoid excessive API calls
        this.locationSearch$.pipe(
            debounceTime(300),
            takeUntil(this.destroy$)
        ).subscribe((term) => {
            this.contractsService.setFilter(term);
            this.contracts = [];
            this.fetchContracts(1, false);
        });
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public launchTender() {
        if (this.loading) {
            return;
        }
        this.loading = true;
        this.tenderService.launch(this.tender).subscribe((tender: Tender) => {
            this.tendersService.fetch().subscribe();
            this.loading = false;
            this.tender = tender;
            this.toast.success(`The task to launch the tenders has been created.
            See details in notification section`);
            this.router.navigateByUrl('/tenders');
        }, (e) => {
            // The server's words (409 launch_in_progress / import_in_progress / step_moved, 422
            // no_locations / too_few_suppliers) reach the toast, and the button comes back
            // (Plan 8 final review I6: it failed silently and stayed disabled).
            this.loading = false;
            this.toast.error(e);
        });
    }

    public calculatePeriod(start_date, end_date): string {
        const startDate = moment(start_date);
        const endDate = moment(end_date).add({ days: 1 });
        const months = endDate.diff(startDate, 'months');
        const days = endDate.subtract(months, 'months').diff(startDate, 'days');
        let period = months ? ` ${months} month${months > 1 ? 's' : ''}` : '';
        period += days ? ` ${days} day${days > 1 ? 's' : ''}` : '';
        return period;
    }

    public stepBack() {
        this.tenderService.stepBack(this.tender)
            .subscribe((tender: Tender) => {
                this.tender = tender;
                this.router.navigateByUrl(`/tenders/new/${tender.id}/suppliers`);
            }, (e) => {
                this.toast.error(e);
            });
    }

    public deleteTender(): void {
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Delete Tender';
        modalRef.componentInstance.text = 'Are you sure you want to delete this tender draft? This action cannot be undone.';

        modalRef.result.then((result) => {
            if (result) {
                this.tenderService.delete(this.tender).subscribe({
                    next: () => {
                        this.toast.success('Tender deleted successfully');
                        this.router.navigate(['/tenders']);
                    },
                    error: (e) => {
                        this.toast.error(e);
                    }
                });
            }
        }, () => { });
    }

    public docDownloadedEvent($emit) {
        this.updateDocuments();
    }

    public updateDocuments() {
        this.documentsService.fetch(this.tender.id).subscribe((documents: Document[]) => {
            this.documents = documents;
        })
    }

    public exportTender() {
        this.tenderExportXlsService.fetch(this.tender);
    }

    public toDate(date: string) {
        return new Date(date);
    }

    public loadMore() {
        this.fetchContracts(this.currentPage + 1, true);
    }

    private fetchContracts(page: number, append: boolean) {
        this.contractsService.setPage(page);

        if (append) {
            this.loadingMore = true;
        } else {
            this.loading = true;
            this.loadingIndicator.show();
        }

        this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
            if (Array.isArray(result)) {
                this.contracts = result;
                this.totalItems = result.length;
                this.currentPage = 1;
            } else {
                if (append) {
                    this.contracts = [...this.contracts, ...result.contracts];
                } else {
                    this.contracts = result.contracts;
                }
                this.totalItems = result.totalItems;
                this.currentPage = result.currentPage;
            }
            this.loading = false;
            this.loadingMore = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingMore = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    // --- Search handlers ---

    public onLocationSearchChange(term: string) {
        this.locationSearch$.next(term);
    }

    public onSupplierSearchChange() {
        this.visibleSupplierCount = 30;
    }

    public get filteredSuppliers(): User[] {
        if (!this.supplierSearch.trim()) {
            return this.tenderInvitedSupplierUsers;
        }
        const q = this.supplierSearch.toLowerCase();
        return this.tenderInvitedSupplierUsers.filter(s =>
            s.company?.name?.toLowerCase().includes(q) ||
            s.firstName?.toLowerCase().includes(q) ||
            s.lastName?.toLowerCase().includes(q) ||
            s.email?.toLowerCase().includes(q)
        );
    }

    public get visibleSuppliers(): User[] {
        return this.filteredSuppliers.slice(0, this.visibleSupplierCount);
    }

    public loadMoreSuppliers() {
        this.visibleSupplierCount += 30;
    }

    // Get unique supplier companies count
    public get uniqueSupplierCompaniesCount(): number {
        const uniqueCompanyIds = new Set(
            this.tenderInvitedSupplierUsers.map(user => user.company.id)
        );
        return uniqueCompanyIds.size;
    }
}
